import React, {
  useCallback, useContext, useEffect, useMemo, useState,
} from 'react';
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  TextField,
  Tooltip,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { getGridStringOperators } from '@mui/x-data-grid';
import { Delete, Edit, Save } from '@mui/icons-material';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';
import { compose } from 'recompose';

import PodContext from '../../../../../context/PodContext/PodContext';
import LoggedInUserContext from '../../../../../context/LoggedInUserContext';
import InternalAssignment from '../../../../../Model/InternalAssignment';
import useComponentMounted from '../../../../../hooks/useComponentMounted';
import Table from '../../../../components/Table';
import useQuickSearch from '../../../../../hooks/useQuickSearch';
import useToast from '../../../../hooks/useToast';
import CoachHistory, { HistoryEventType } from '../../../../Model/CoachHistory';
import CoachPod from '../../../../../Model/CoachPod';

import FirebaseContext from '../../../../../context/FirebaseContext';
import {
  ActionButtonContainer,
  WarningIcon,
  DialogContentContainer,
  MaxUsersContainer,
  CapacityLabel,
  EditContainer,
  EditInput,
  NameDisplay,
  SavingText,
} from './styles';

import texts from './texts.json';

const CAPACITY_STATUS = {
  GOOD: 'green',
  WARNING: 'amber',
  CRITICAL: 'red',
};

/**
 * Percentage of the pod's max users that is currently in use, or null when the pod has no max users
 * configured. Measured against the clients that consume capacity: paused clients keep their pod
 * assignment, so counting them here is what made every pod read as full.
 */
const getUtilizedCapacity = ({ activeUserCount, maxUsers }) => {
  if (!maxUsers) {
    return null;
  }

  return Math.round(((activeUserCount ?? 0) / maxUsers) * 100);
};

const getCapacityStatus = (percentage) => {
  if (percentage > 100) {
    return CAPACITY_STATUS.CRITICAL;
  }
  if (percentage > 80) {
    return CAPACITY_STATUS.WARNING;
  }
  return CAPACITY_STATUS.GOOD;
};

const CoachPodTable = ({ selectedCoach, podClientCounts, refreshPodClientCounts }) => {
  const { pods, fetchPodsForCoach, assistantCoaches } = useContext(PodContext);
  const {
    userDoc: { name: authUserName },
    userId: authUserId,
  } = useContext(LoggedInUserContext);
  const {
    firebase: { remote },
  } = useContext(FirebaseContext);

  const [podsWithAssignments, setPodsWithAssignments] = useState([]);
  const [isPodDeleting, setIsPodDeleting] = useState(false);
  const [isPodUpdating, setIsPodUpdating] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [updateACDialogOpen, setUpdateACDialogOpen] = useState(false);
  const [selectedPodId, setSelectedPodId] = useState(null);
  const [selectedACs, setSelectedACs] = useState([]);
  const [maxUsers, setMaxUsers] = useState(0);
  const [isMaxUsersUpdating, setIsMaxUsersUpdating] = useState(false);
  const [editingPod, setEditingPod] = useState({ id: null, name: null });
  const [isSavingPodName, setIsSavingPodName] = useState(false);
  const [columnVisibilityModel, setColumnVisibilityModel] = useState({});

  const isComponentMountedRef = useComponentMounted();
  const { showToast } = useToast();

  useEffect(() => {
    const init = async () => {
      const assistantAssignments = await InternalAssignment.getAllCurrentAssignments(null, 'AC', selectedCoach.id);

      if (!isComponentMountedRef.current) return;

      const assistantMap = assistantAssignments.docs.reduce((map, assignment) => {
        const { podId, userId, userName } = assignment;

        if (podId) {
          if (!map.has(podId)) {
            map.set(podId, []);
          }
          map.get(podId).push({ userId, userName });
        }

        return map;
      }, new Map());

      /*
        Both counts are derived from the same client query, so the pair always reconciles. In
        particular the assigned figure is NOT the pod document's `userCount`: that is an
        increment/decrement counter which drifts between rebalances, so a pod with no members at all
        can still store a large number.
      */
      const { activeByPod, assignedByPod } = podClientCounts;

      const newPods = pods.map((pod) => {
        const {
          id, name, coach, maxUsers: maxUsersData,
        } = pod;
        const assignments = assistantMap.get(id) || [];

        return {
          id,
          name,
          activeUserCount: activeByPod?.[id] ?? 0,
          assignedUserCount: assignedByPod?.[id] ?? 0,
          coach,
          assistantCoaches: assignments,
          maxUsers: maxUsersData,
        };
      });

      if (isComponentMountedRef.current) {
        setPodsWithAssignments(newPods);
      }
    };

    init();
  }, [isComponentMountedRef, pods, selectedCoach.id, podClientCounts]);

  const addACtoPod = useCallback(
    async (acId) => {
      try {
        setIsPodUpdating(true);
        const assignments = await InternalAssignment.getAllCurrentAssignments(acId, 'AC', selectedCoach.id);
        await Promise.all(assignments.docs?.map((assignment) => assignment.updateFields({ podId: selectedPodId })));

        await CoachHistory.addCoachHistoryItem({
          type: HistoryEventType.COACH_POD_ASSISTANT_ADDED,
          coachId: selectedCoach.id,
          actionedBy: authUserId,
          actionedByUserName: authUserName,
          metadata: {
            podId: selectedPodId,
            assistantCoach: acId,
          },
        });
      } catch (error) {
        showToast(texts.assignmentUpdateError, { error: true });
      } finally {
        setIsPodUpdating(false);
      }
    },
    [authUserId, authUserName, selectedCoach.id, selectedPodId, showToast],
  );

  const removeACFromPod = useCallback(
    async (acId) => {
      try {
        setIsPodUpdating(true);
        const assignment = await InternalAssignment.getAssignmentsByPodIdAnsAC(selectedPodId, acId);
        assignment?.updateFields({ podId: null });

        await CoachHistory.addCoachHistoryItem({
          type: HistoryEventType.COACH_POD_ASSISTANT_REMOVED,
          coachId: selectedCoach.id,
          actionedBy: authUserId,
          actionedByUserName: authUserName,
          metadata: {
            podId: selectedPodId,
            assistantCoach: acId,
          },
        });
      } catch (error) {
        showToast(texts.assignmentUpdateError, { error: true });
      } finally {
        setIsPodUpdating(false);
      }
    },
    [authUserId, authUserName, selectedCoach.id, selectedPodId, showToast],
  );

  const handleACSelect = useCallback(
    async (event) => {
      const {
        target: { value },
      } = event;
      const addedAC = value.find((item) => !selectedACs.includes(item));
      const removedAC = selectedACs.find((item) => !value.includes(item));

      if (addedAC) {
        await addACtoPod(addedAC);
      }

      if (removedAC) {
        await removeACFromPod(removedAC);
      }
      setSelectedACs(value);
    },
    [addACtoPod, removeACFromPod, selectedACs],
  );

  const handleDeletePodModalOpen = useCallback(
    (podId) => {
      setDeleteDialogOpen(true);
      setSelectedPodId(podId);
    },
    [setSelectedPodId],
  );

  const handleDeletePodModalClose = () => {
    setDeleteDialogOpen(false);
  };

  const handleUpdateACModalOpen = useCallback(
    (podId) => {
      const selectedPod = podsWithAssignments.find((pod) => pod.id === podId);

      const currentACIds = selectedPod?.assistantCoaches?.map((ac) => ac.userId) || [];

      const currentMaxUsers = selectedPod?.maxUsers || 0;

      setSelectedACs(currentACIds);
      setMaxUsers(currentMaxUsers);
      setUpdateACDialogOpen(true);
      setSelectedPodId(podId);
    },
    [podsWithAssignments],
  );

  const handleUpdateACModalClose = async () => {
    await fetchPodsForCoach(selectedCoach.id);
    setSelectedACs([]);
    setMaxUsers(0);
    setUpdateACDialogOpen(false);
  };

  const applyStringOperator = (text, searchValue, operator) => {
    switch (operator) {
      case 'contains':
        return text.includes(searchValue);
      case 'equals':
        return text === searchValue;
      case 'startsWith':
        return text.startsWith(searchValue);
      case 'endsWith':
        return text.endsWith(searchValue);
      case 'isAnyOf':
        return Array.isArray(searchValue) ? searchValue.includes(text) : false;
      default:
        return text.includes(searchValue);
    }
  };

  const getAcsDisplayValue = (acs) => (acs.length === 0 ? texts.notAssigned : acs.map((ac) => ac.userName).join(', '));

  const createACsFilterFn = useCallback((filterItem) => {
    if (!filterItem.field) return null;

    return (params) => {
      const acs = params.row.assistantCoaches;
      const { value: searchValue, operator: operatorValue } = filterItem;

      if (operatorValue === 'isEmpty') return acs.length === 0;
      if (operatorValue === 'isNotEmpty') return acs.length > 0;
      if (!searchValue) return true;

      if (acs.length === 0) {
        const notAssignedText = texts.notAssigned.toLowerCase();
        const searchLower = typeof searchValue === 'string' ? searchValue.toLowerCase() : searchValue;
        return applyStringOperator(notAssignedText, searchLower, operatorValue);
      }

      return acs.some((coach) => {
        const coachName = coach.userName.toLowerCase();
        const searchLower = typeof searchValue === 'string' ? searchValue.toLowerCase() : searchValue;
        return applyStringOperator(coachName, searchLower, operatorValue);
      });
    };
  }, []);

  const handlePodDelete = useCallback(async () => {
    try {
      setIsPodDeleting(true);

      const response = await remote(
        `coachPod-initializePods/pod/${encodeURIComponent(selectedPodId)}`,
        {},
        { method: 'DELETE' },
      );

      await CoachHistory.addCoachHistoryItem({
        type: HistoryEventType.COACH_POD_DELETED,
        coachId: selectedCoach.id,
        actionedBy: authUserId,
        actionedByUserName: authUserName,
        metadata: {
          podId: selectedPodId,
        },
      });

      setDeleteDialogOpen(false);

      if (response.ok) {
        showToast(texts.podDeleted, { success: true });

        /*
          Deleting a pod rebalances the whole coach server-side, so its members move. The pods
          collection is fetched with `mode: 'off'`, so nothing here updates on its own — refresh both
          together or the table shows one pod's new membership against the old client counts.
        */
        await Promise.all([fetchPodsForCoach(selectedCoach.id), refreshPodClientCounts(selectedCoach.id)]);
      } else {
        const err = await response.json();
        showToast(err.error, { error: true });
      }
    } catch (error) {
      showToast(texts.podDeletionError, { error: true });
    } finally {
      setIsPodDeleting(false);
      setSelectedPodId(null);
    }
  }, [
    remote,
    selectedPodId,
    selectedCoach.id,
    authUserId,
    authUserName,
    showToast,
    fetchPodsForCoach,
    refreshPodClientCounts,
  ]);

  const handleSavePodName = useCallback(
    async (podId) => {
      try {
        setIsSavingPodName(true);
        await CoachPod.updatePodName(podId, editingPod.name);
        await fetchPodsForCoach(selectedCoach.id);
        showToast(texts.podNameUpdated, { success: true });
        setEditingPod({ id: null, name: null });
      } catch (error) {
        showToast(texts.podNameUpdateError, { error: true });
      } finally {
        setIsSavingPodName(false);
      }
    },
    [editingPod.name, selectedCoach.id, fetchPodsForCoach, showToast],
  );

  const columns = useMemo(
    () => [
      {
        field: 'name',
        quickSearch: true,
        headerName: texts.podName,
        flex: 30,
        renderCell: ({ row }) => {
          const isEditing = editingPod.id === row.id;

          return (
            <EditContainer>
              {isEditing ? (
                <EditInput
                  value={editingPod.name || ''}
                  onChange={(e) => setEditingPod((prev) => ({ ...prev, name: e.target.value }))}
                  onKeyDown={(e) => {
                    e.stopPropagation();
                  }}
                />
              ) : (
                <NameDisplay>
                  {row.assistantCoaches.length === 0 && (
                    <Tooltip
                      title={(
                        <>
                          {texts.noAssistantCoachesTooltip.line1}
                          <br />
                          {texts.noAssistantCoachesTooltip.line2}
                        </>
                      )}
                      placement="top"
                      arrow
                    >
                      <WarningIcon fontSize="small" />
                    </Tooltip>
                  )}
                  {row.name}
                </NameDisplay>
              )}
              {isEditing ? (
                <IconButton size="small" onClick={() => handleSavePodName(editingPod.id)} disabled={isSavingPodName}>
                  {isSavingPodName ? <SavingText>{texts.saving}</SavingText> : <Save fontSize="small" />}
                </IconButton>
              ) : (
                <IconButton
                  size="small"
                  onClick={() => {
                    setEditingPod({ id: row.id, name: row.name });
                  }}
                >
                  <Edit fontSize="small" />
                </IconButton>
              )}
            </EditContainer>
          );
        },
      },
      {
        field: 'activeUserCount',
        quickSearch: true,
        headerName: texts.activeUserCount,
        flex: 20,
      },
      {
        field: 'assignedUserCount',
        quickSearch: true,
        headerName: texts.assignedUserCount,
        flex: 20,
      },
      {
        field: 'maxUsers',
        quickSearch: true,
        headerName: texts.maxUsers,
        flex: 15,
        renderCell: ({ row }) => row.maxUsers,
      },
      {
        field: 'utilizedCapacity',
        headerName: texts.utilizedCapacity,
        flex: 20,
        type: 'number',
        align: 'left',
        headerAlign: 'left',
        valueGetter: ({ row }) => getUtilizedCapacity(row),
        renderCell: ({ value }) => {
          if (value === null) {
            return '';
          }

          return <CapacityLabel className={getCapacityStatus(value)}>{`${value} %`}</CapacityLabel>;
        },
      },
      {
        field: 'assistantCoaches',
        quickSearch: true,
        headerName: texts.assistantCoaches,
        flex: 30,
        stringifier: getAcsDisplayValue,
        valueGetter: ({ row }) => getAcsDisplayValue(row.assistantCoaches),
        filterOperators: getGridStringOperators().map((operator) => ({
          ...operator,
          getApplyFilterFn: createACsFilterFn,
        })),
        renderCell: ({ row }) => getAcsDisplayValue(row.assistantCoaches),
      },
      {
        field: 'action',
        quickSearch: true,
        headerName: texts.action,
        flex: 12,
        minWidth: 150,
        disableColumnMenu: true,
        sortable: false,
        filterable: false,
        renderCell: ({ row }) => (
          <ActionButtonContainer>
            <Button
              onClick={() => handleUpdateACModalOpen(row.id)}
              variant="contained"
              size="small"
              startIcon={<Edit />}
            >
              {texts.update}
            </Button>
            <IconButton
              onClick={() => handleDeletePodModalOpen(row.id)}
              disabled={row.assistantCoaches.length > 0 || isPodDeleting}
            >
              <Delete />
            </IconButton>
          </ActionButtonContainer>
        ),
      },
    ],
    [
      editingPod,
      isSavingPodName,
      handleSavePodName,
      createACsFilterFn,
      isPodDeleting,
      handleUpdateACModalOpen,
      handleDeletePodModalOpen,
    ],
  );

  const { filteredRows } = useQuickSearch(podsWithAssignments, columns);

  const getSelectedNames = useCallback(
    (ids) => assistantCoaches
      .filter((coach) => ids.includes(coach.id))
      .map((coach) => coach.firstName)
      .join(', '),
    [assistantCoaches],
  );

  const handleMaxUsersChange = (event) => {
    const value = parseInt(event.target.value, 10) || 0;
    setMaxUsers(value);
  };

  const updateMaxUsers = async () => {
    try {
      setIsMaxUsersUpdating(true);
      await CoachPod.updateMaxUsers(selectedPodId, maxUsers);
      showToast(texts.maxUsersUpdated, { success: true });
    } catch (error) {
      showToast(texts.maxUsersUpdateError, { error: true });
    } finally {
      setIsMaxUsersUpdating(false);
    }
  };

  const getRowClassName = useCallback((params) => params.row.assistantCoaches.length === 0 && 'no-ac-assigned', []);

  return (
    <>
      <Table
        columns={columns}
        rows={filteredRows}
        getRowClassName={getRowClassName}
        columnVisibilityModel={columnVisibilityModel}
        onColumnVisibilityModelChange={setColumnVisibilityModel}
      />

      <Dialog open={updateACDialogOpen} onClose={handleUpdateACModalClose}>
        <DialogTitle id="update-ac">{texts.updateACTitle}</DialogTitle>
        <DialogContent>
          <DialogContentContainer>
            <DialogContentText id="alert-dialog-description">{texts.updatePodConfirmation}</DialogContentText>
            <FormControl fullWidth>
              <InputLabel id="ac-select-label">{texts.assistantCoaches}</InputLabel>
              <Select
                labelId="ac-select-label"
                value={selectedACs}
                label={texts.assistantCoaches}
                onChange={handleACSelect}
                multiple
                renderValue={(selected) => getSelectedNames(selected)}
              >
                {assistantCoaches.map((coach) => (
                  <MenuItem key={coach.id} value={coach.id}>
                    <Checkbox disabled={isPodUpdating} checked={selectedACs.includes(coach.id)} />
                    <ListItemText primary={coach.firstName} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <MaxUsersContainer>
              <TextField
                margin="dense"
                id="maxUsers"
                label={texts.maxUsers}
                fullWidth
                type="number"
                inputProps={{
                  min: 1,
                  step: 1,
                  pattern: '[1-9][0-9]*',
                }}
                size="small"
                onChange={handleMaxUsersChange}
                value={maxUsers === 0 ? '' : maxUsers}
                onKeyDown={(e) => {
                  // Prevent decimal point, minus sign, 'e', and leading zeros
                  if (
                    e.key === '.'
                    || e.key === '-'
                    || e.key === 'e'
                    || e.key === 'E'
                    || (e.key === '0' && e.target.value === '')
                  ) {
                    e.preventDefault();
                  }
                }}
              />
              <LoadingButton
                disabled={maxUsers === 0}
                variant="contained"
                onClick={updateMaxUsers}
                loading={isMaxUsersUpdating}
              >
                {texts.update}
              </LoadingButton>
            </MaxUsersContainer>
          </DialogContentContainer>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={handleUpdateACModalClose}>
            {texts.ok}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeletePodModalClose}
        aria-labelledby="delete-pod"
        aria-describedby="delete-pod-confirmation"
      >
        <DialogTitle id="delete-pod">{texts.deletePod}</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">{texts.deletePodConfirmation}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeletePodModalClose}>{texts.cancel}</Button>
          <LoadingButton
            loading={isPodDeleting}
            disabled={isPodDeleting}
            variant="contained"
            onClick={handlePodDelete}
            autoFocus
          >
            {texts.delete}
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </>
  );
};

CoachPodTable.propTypes = {
  selectedCoach: PropTypes.object.isRequired,
  podClientCounts: PropTypes.object.isRequired,
  refreshPodClientCounts: PropTypes.func.isRequired,
};

export default compose(observer)(CoachPodTable);
